import React from "react";

import styled from "styled-components/macro";

import { NavLink, useParams } from "react-router-dom";

import { Helmet } from "react-helmet-async";

import { blue } from "@material-ui/core/colors";

import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";

import {
  Box as MuiBox,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Divider as MuiDivider,
  Grid,
  IconButton,
  Link,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";

import * as Icons from "@material-ui/icons";

import { spacing } from "@material-ui/system";

import { BpProjects } from "../../services";

const Box = styled(MuiBox)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Paper = styled(MuiPaper)(spacing);

export const ProjectsAccreditationBrokersPage = () => {
  const [project, setProject] = React.useState(null);

  const [page, setPage] = React.useState(0);
  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [data, setData] = React.useState([]);

  const { projectId } = useParams();

  React.useEffect(() => {
    (async () => {
      const resProject = await BpProjects.getById(projectId);
      setProject(resProject.data);
    })();
  }, [projectId]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  React.useEffect(() => {
    (async () => {
      const resList = await BpProjects.listAccreditations(
        projectId,
        page,
        debouncedSearch
      );
      setData(resList.data);
    })();
  }, [projectId, page, debouncedSearch]);

  const handleSearchChange = (event) => {
    setSearch(event?.target?.value);
  };

  const [exporting, setExporting] = React.useState(false);

  const handleExport = async () => {
    try {
      setExporting(true);

      const res = await BpProjects.exportAccreditations(projectId);

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `corretores-credenciados-${project?.name || projectId}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert("Não foi possível exportar o CSV");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box p={8}>
      <Helmet title="Corretores Credenciados" />

      <Grid justify="space-between" container>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Lista de Corretores Credenciados
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} exact to="/bp/projects">
              Empreendimentos
            </Link>
            <Link
              component={NavLink}
              exact
              to={`/bp/projects/${projectId}/edit`}
            >
              {project?.name}
            </Link>
            <Typography>Corretores Credenciados</Typography>
          </Breadcrumbs>
        </Grid>

        <Grid item xs={5}>
          <Grid container spacing={4} alignItems="center" wrap="nowrap">
            <Grid item xs>
              <TextField
                id="filter"
                label="Filtro (nome, e-mail, CRECI ou imobiliária)"
                variant="outlined"
                fullWidth
                value={search}
                onChange={handleSearchChange}
                my={2}
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Icons.CloudDownload />}
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? "Exportando..." : "Exportar CSV"}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <div>
            <Paper>
              <TableContainer>
                <Table aria-labelledby="tableTitle" size={"medium"}>
                  <TableHead>
                    <TableRow>
                      <TableCell>CORRETOR</TableCell>
                      <TableCell>IMOBILIÁRIA</TableCell>
                      <TableCell>STATUS</TableCell>
                      <TableCell>CREDENCIADO EM</TableCell>
                      <TableCell align="center" style={{ minWidth: "150px" }}>
                        Ações
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.rows?.map((row, index) => {
                      return (
                        <TableRow
                          style={{
                            background:
                              row.status === "enabled" ? "#f0fff0" : "#ffeeed",
                          }}
                        >
                          <TableCell align="left" id={index} scope="row">
                            <Link
                              component={NavLink}
                              to={`/bp/brokers/${row.broker.id}/accreditations/${row.id}`}
                            >
                              <strong>{row.broker.name}</strong>
                            </Link>
                            <Box>
                              <small>{row.broker.email}</small>
                            </Box>
                          </TableCell>

                          <TableCell align="left" id={index} scope="row">
                            {row.realState || "-"}
                            {row.realStateCreci && (
                              <Box>
                                <small>CRECI: {row.realStateCreci}</small>
                              </Box>
                            )}
                          </TableCell>

                          <TableCell align="left" id={index} scope="row">
                            {row.status === "enabled"
                              ? "Habilitado"
                              : "Não habilitado"}
                          </TableCell>

                          <TableCell align="left" id={index} scope="row">
                            {row.createdAt &&
                              format(parseISO(row.createdAt), "P", {
                                locale: pt,
                              })}
                          </TableCell>

                          <TableCell
                            align="right"
                            style={{ minWidth: "150px" }}
                          >
                            <Tooltip title="Editar credenciamento">
                              <IconButton
                                component={NavLink}
                                to={`/bp/brokers/${row.broker.id}/accreditations/${row.id}`}
                                style={{ color: blue[700] }}
                              >
                                <Icons.Create />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                labelDisplayedRows={({ from, to, count }) =>
                  `Página ${page + 1} de ${Math.ceil(count / 100)}`
                }
                rowsPerPageOptions={[100]}
                component="div"
                count={data.count || 0}
                rowsPerPage={100}
                page={page}
                onChangePage={(_, page) => setPage(page)}
              />
            </Paper>
          </div>
        </Grid>
      </Grid>
    </Box>
  );
};
